import { Button, Center, FormControl, FormErrorMessage, Input, Stack, useColorModeValue } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { GoogleSignInButton } from ".";
import { useButtonProps } from "hooks";
import { useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";

interface Props {
  onSubmit: (fields: any) => void;
}

export const LoginFormForModal = (props: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    username: string;
    password: string;
  }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const commonButtonProps = useButtonProps("md", "cta");
  const [hasWebsocketSupport, setHasWebsocketSupport] = useState(true);
  const lastError = useSelector((state: RootState) => state.session.lastError);
  const inputBgColor = useColorModeValue("white", "gray.700");
  const inputBorderColor = useColorModeValue("gray.300", "gray.500");

  const checkWebSocketSupport = () => {
    if ("WebSocket" in window) {
      setHasWebsocketSupport(true);
    } else {
      setHasWebsocketSupport(false);
    }
  };

  useEffect(() => {
    checkWebSocketSupport();
  }, []);

  useEffect(() => {
    if (lastError?.includes("The username or password is incorrect.")) {
      setIsSubmitting(false);
    }
    if (lastError) console.log(`Last error: ${lastError}`);
  }, [lastError]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsSubmitting(false);
    }, 10000);
    return () => clearTimeout(timeout);
  }, [isSubmitting]);

  return (
    <Stack width="100%" spacing={6}>
      <form onSubmit={handleSubmit(props.onSubmit)}>
        <Stack spacing={6}>
          <FormControl isInvalid={!!errors.username}>
            <Input
              _hover={{}}
              size="md"
              {...register("username", { required: true })}
              id="login-username"
              borderColor={errors.username ? "red.500" : inputBorderColor}
              bg={inputBgColor}
              name="username"
              type="username"
              placeholder="Email Address"
              autoComplete="username"
            />
            {errors.username && <FormErrorMessage>Email is required.</FormErrorMessage>}
          </FormControl>
          <FormControl isInvalid={!!errors.password}>
            <Input
              _hover={{}}
              size="md"
              {...register("password", { required: true })}
              id="login-password"
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              bg={inputBgColor}
              borderColor={errors.password ? "red.500" : inputBorderColor}
              fontSize="md"
              boxShadow="none"
            />
            {errors.password && <FormErrorMessage>Password is required.</FormErrorMessage>}
          </FormControl>
          <Center mt="0.9rem">
            <Button
              {...commonButtonProps}
              borderColor={isSubmitting ? "#81c34c" : "primary.default"}
              bgColor={isSubmitting ? "#81c34c" : "primary.default"}
              opacity={isSubmitting ? 0.6 : 1}
              cursor={isSubmitting ? "default" : "pointer"}
              onClick={() => {
                setIsSubmitting(true);
              }}
              width="20rem"
              mx="auto"
              size="lg"
              borderRadius={"full"}
              id={"login-submit"}
              type="submit"
              isDisabled={!hasWebsocketSupport}>
              {isSubmitting ? "SIGNING IN..." : "SIGN IN"}
            </Button>
          </Center>
        </Stack>
      </form>
      <Stack spacing={"1rem"} mt="4rem" width="20rem" mx="auto">
        <GoogleSignInButton variant={"signin"} />
      </Stack>
    </Stack>
  );
};
